import React, { useState } from 'react';
import { Button, Modal, Input, Alert, Form } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { discoverSsoProvider } from '../../actions/user';

type SSOLoginButtonProps = {
  label?: string;
  block?: boolean;
};

const SSOLoginButton: React.FC<SSOLoginButtonProps> = ({ label, block = true }) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenModal = () => {
    setModalVisible(true);
    setEmail('');
    setError(null);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEmail('');
    setError(null);
    setLoading(false);
  };

  const handleEmailSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError(t('login:ssoInvalidEmail'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await discoverSsoProvider(email);

      if (result.sso_available && result.authorization_url) {
        // Redirect to corporate IdP
        window.location.href = result.authorization_url;
      } else {
        setError(result.message || t('login:ssoNotAvailable'));
        setLoading(false);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || t('login:ssoError');
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handlePressEnter = () => {
    if (!loading) {
      handleEmailSubmit();
    }
  };

  return (
    <>
      <Button onClick={handleOpenModal} size="large" block={block}>
        {label || t('login:ssoLogin')}
      </Button>

      <Modal
        title={t('login:ssoTitle')}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            {t('common:cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleEmailSubmit}
            disabled={!email}
          >
            {t('login:ssoContinue')}
          </Button>,
        ]}
      >
        <p className="mb-4">{t('login:ssoEmailDescription')}</p>
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form layout="vertical">
          <Form.Item label={t('login:ssoEmailLabel')}>
            <Input
              prefix={<MailOutlined />}
              type="email"
              size="large"
              placeholder={t('common:email')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              onPressEnter={handlePressEnter}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SSOLoginButton;
